/**
 * Check for Android app updates from the frontend.
 *
 * Fetches version.json from CloudFront and compares against the
 * installed app version via the Capacitor App plugin.
 * Returns null when not running inside the Android wrapper.
 */

import { getEnvironment } from './environment';

const VERSION_JSON_URLS: Record<string, string> = {
  production: 'https://talkyn.ai.xxz.dk/app/talkyn-version.json',
  staging: 'https://staging.talkyn.ai.xxz.dk/app/talkyn-version.json',
};

export interface UpdateCheckResult {
  updateAvailable: boolean;
  /** e.g. "1.2.0 (20)" */
  currentVersion: string;
  /** e.g. "1.2.3" */
  latestVersion: string;
  latestVersionCode: number;
  downloadUrl: string;
}

/**
 * Check whether a newer Android APK is available.
 * Returns null if not running in Capacitor Android.
 */
export async function checkForAppUpdate(): Promise<UpdateCheckResult | null> {
  try {
    // Detect Capacitor Android — same pattern as useVersionInfo.ts
    const cap = (window as unknown as Record<string, unknown>).Capacitor as
      | { getPlatform?: () => string; Plugins?: Record<string, unknown> }
      | undefined;
    if (!cap?.getPlatform || cap.getPlatform() !== 'android') return null;

    // Get current version from Capacitor App plugin
    const appPlugin = cap.Plugins?.App as
      | { getInfo?: () => Promise<{ version: string; build: string }> }
      | undefined;
    if (!appPlugin?.getInfo) return null;

    const info = await appPlugin.getInfo();
    const currentVersionCode = parseInt(info.build, 10);
    if (isNaN(currentVersionCode)) return null;

    // Fetch version.json for current environment
    const env = getEnvironment();
    const url = VERSION_JSON_URLS[env] ?? VERSION_JSON_URLS.staging;
    const res = await fetch(url, { signal: AbortSignal.timeout(10_000) });
    if (!res.ok) return null;

    const data = (await res.json()) as {
      versionCode: number;
      versionName: string;
      downloadUrl: string;
    };

    return {
      updateAvailable: data.versionCode > currentVersionCode,
      currentVersion: `${info.version} (${info.build})`,
      latestVersion: data.versionName,
      latestVersionCode: data.versionCode,
      downloadUrl: data.downloadUrl,
    };
  } catch {
    return null;
  }
}
